/**
 * serverConfigService
 *
 * Reads Synapse server-side configuration that the web client needs to honour
 * at runtime. Currently only fetches the inactive-room deletion threshold
 * exposed by Synapse-Pro's inactive_room_deletion module:
 *
 *   GET <homeserverUrl>/_synapse/client/inactive_room_deletion_module_config
 *   → { inactive_time_before_deletion_ms: number|null, ... }
 *
 * Failure modes are absorbed silently — dev environments that lack the module
 * MUST NOT surface an error to the user. The cached value falls back to 180
 * days on any non-401 failure. A 401 deliberately does NOT poison the cache
 * so the token-refresh layer can recover; subsequent callers retry the fetch.
 *
 * Cross-user bleed protection: a logout-driven `resetInactiveDeletionConfig()`
 * aborts any in-flight request AND bumps a generation counter so that a
 * fetch which resolves after reset cannot retroactively overwrite cachedDays.
 */
import { timFetch } from '../utils/timUserAgent';
import matrixService from './matrixService';
import { MS_PER_DAY } from '../utils/timeUtils';
import logger from '../utils/logger';

const FETCH_TIMEOUT_MS = 5000;
const MAX_REASONABLE_DAYS = 3650; // 10 years — defence against malformed server values
export const FALLBACK_DELETION_DAYS = 180;

let cachedDays = null;        // null = not yet resolved
let inflightPromise = null;   // dedupes concurrent callers
let activeController = null;  // so reset() can abort an in-flight fetch
let generation = 0;           // bumped by reset(); stale runs MUST NOT write

export const initInactiveDeletionConfig = () => {
  if (cachedDays !== null) return Promise.resolve(cachedDays);
  if (inflightPromise) return inflightPromise;

  const myGeneration = generation;
  const controller = new AbortController();
  activeController = controller;

  const promise = (async () => {
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const client = matrixService.getClient();
      if (!client) throw new Error('no-client');
      const baseUrl = typeof client.getHomeserverUrl === 'function' ? client.getHomeserverUrl() : null;
      const token = typeof client.getAccessToken === 'function' ? client.getAccessToken() : null;
      if (!baseUrl || !token) throw new Error('no-creds');

      // Trim a trailing slash on the baseUrl so we don't emit a `//` path.
      const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const url = `${trimmedBase}/_synapse/client/inactive_room_deletion_module_config`;

      const resp = await timFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        redirect: 'error',
      });

      // If a logout-driven reset happened mid-fetch, do not write cachedDays.
      if (myGeneration !== generation) return FALLBACK_DELETION_DAYS;

      if (resp.status === 401) {
        logger.warn('[serverConfigService] 401 on inactive_room_deletion_module_config; not caching');
        throw new Error('http-401-skip-cache');
      }
      if (!resp.ok) throw new Error(`http-${resp.status}`);

      const data = await resp.json();
      if (myGeneration !== generation) return FALLBACK_DELETION_DAYS;

      const ms = data?.inactive_time_before_deletion_ms;
      if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) {
        throw new Error('invalid-ms');
      }
      const rawDays = Math.round(ms / MS_PER_DAY);
      if (rawDays <= 0) throw new Error('zero-days');
      cachedDays = Math.min(rawDays, MAX_REASONABLE_DAYS);
      return cachedDays;
    } catch (err) {
      // Re-throw the 401 so the outer .catch below can leave cachedDays null
      // (i.e. NOT cache the fallback) — token-refresh layer recovers and the
      // next caller will retry the fetch.
      if (err?.message === 'http-401-skip-cache') throw err;
      logger.debug(
        '[serverConfigService] Falling back to default deletion threshold:',
        err?.name === 'AbortError' ? 'timeout' : (err?.message || err),
      );
      // Don't poison the cache if a logout-driven reset happened mid-fetch.
      if (myGeneration === generation) cachedDays = FALLBACK_DELETION_DAYS;
      return FALLBACK_DELETION_DAYS;
    } finally {
      clearTimeout(timeoutId);
      if (activeController === controller) activeController = null;
      // Null inflightPromise only when we're still the active generation —
      // otherwise a concurrent reset() + re-init may have installed a fresh
      // one which we must not clobber.
      if (myGeneration === generation) inflightPromise = null;
    }
  })().catch((err) => {
    // Awaiters don't care about the 401 distinction; return the fallback
    // without populating the cache so the next call retries.
    if (err?.message === 'http-401-skip-cache') return FALLBACK_DELETION_DAYS;
    throw err;
  });

  inflightPromise = promise;
  return inflightPromise;
};

export const getInactiveDeletionDays = () =>
  (cachedDays === null ? FALLBACK_DELETION_DAYS : cachedDays);

export const resetInactiveDeletionConfig = () => {
  cachedDays = null;
  inflightPromise = null;
  generation++;
  if (activeController) {
    try { activeController.abort(); } catch { /* ignore */ }
    activeController = null;
  }
};

// Dev/test-only escape hatch; tree-shaken from production bundles.
export const __testing__ = process.env.NODE_ENV !== 'production' ? {
  reset: resetInactiveDeletionConfig,
  getCachedDays: () => cachedDays,
  getGeneration: () => generation,
} : undefined;

export default {
  initInactiveDeletionConfig,
  getInactiveDeletionDays,
  resetInactiveDeletionConfig,
};
